import { Check, ArrowRight } from "lucide-react";
import { PRICING, ADDONS } from "../../data/content";
import { ChapterHeading } from "./ChapterHeading";
import { Reveal } from "./Reveal";
import { scrollToId } from "../../lib/scroll";

export const Pricing = () => (
    <section
        id="pricing"
        data-testid="pricing-section"
        className="py-24 lg:py-32 bg-[#0A0E17]/60 border-y border-white/5"
    >
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
            <ChapterHeading
                label="Pricing"
                title="Simple Starting Prices"
                sub="These are starting prices only. Actual project price depends on requirements and scope — shared transparently before work begins."
                align="center"
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-7 items-stretch">
                {PRICING.map((tier, i) => (
                    <Reveal key={tier.name} delay={i * 0.1} className="h-full">
                        <div
                            data-testid={`pricing-card-${tier.name.toLowerCase()}`}
                            className={`relative h-full rounded-3xl p-7 flex flex-col transition-all duration-300 ${
                                tier.popular
                                    ? "bg-[#0D131F] border-2 border-[#F5C518] glow-gold lg:-translate-y-3"
                                    : "bg-[#0D131F] border border-white/10 hover:border-[#00E5FF]/35"
                            }`}
                        >
                            {tier.popular && (
                                <span
                                    data-testid="pricing-popular-badge"
                                    className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-[#F5C518] px-4 py-1 font-jbmono text-[10px] font-bold uppercase tracking-[0.2em] text-[#07090E]"
                                >
                                    Most Chosen
                                </span>
                            )}
                            <p className="font-jbmono text-[11px] uppercase tracking-[0.28em] text-[#00E5FF]">
                                {tier.name}
                            </p>
                            <div className="mt-4 flex items-end gap-2">
                                <span className="font-display text-4xl font-extrabold text-white">
                                    {tier.price}
                                </span>
                                <span className="pb-1.5 text-xs text-slate-500">onwards</span>
                            </div>
                            <p className="mt-1 text-[11px] text-slate-500 font-jbmono tracking-wide">
                                Starting from — final quote depends on scope
                            </p>
                            <ul className="mt-6 space-y-2.5 flex-1">
                                {tier.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                                        <Check
                                            size={15}
                                            className={`mt-0.5 shrink-0 ${tier.popular ? "text-[#F5C518]" : "text-[#00E5FF]"}`}
                                        />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                            <button
                                data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                                onClick={() => scrollToId("#contact")}
                                className={`mt-7 w-full rounded-full px-6 py-3.5 text-sm font-semibold transition-all duration-300 active:scale-[0.98] ${
                                    tier.popular
                                        ? "bg-[#F5C518] text-[#07090E] hover:bg-[#E5B50F]"
                                        : "border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10"
                                }`}
                            >
                                Discuss Your Project
                            </button>
                        </div>
                    </Reveal>
                ))}
            </div>

            <Reveal className="mt-14">
                <div
                    data-testid="custom-quote-band"
                    className="rounded-3xl border border-[#00E5FF]/25 bg-gradient-to-r from-[#0D131F] via-[#0D131F] to-[#0A1A2E] p-7 sm:p-9 flex flex-col sm:flex-row sm:items-center gap-6 justify-between"
                >
                    <div>
                        <h3 className="font-display text-xl sm:text-2xl font-bold text-white">
                            Need a custom solution?
                        </h3>
                        <p className="mt-2 text-sm text-slate-400 max-w-xl leading-relaxed">
                            Custom web apps, mobile apps, automation and advanced business systems are
                            available based on requirements.
                        </p>
                    </div>
                    <button
                        data-testid="custom-quote-cta"
                        onClick={() => scrollToId("#contact")}
                        className="group inline-flex items-center gap-2 self-start sm:self-auto rounded-full bg-[#F5C518] px-7 py-3.5 text-sm font-semibold text-[#07090E] transition-all duration-300 hover:bg-[#E5B50F] active:scale-95 shrink-0"
                    >
                        Get Custom Quote
                        <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                </div>
            </Reveal>

            <div className="mt-16">
                <Reveal>
                    <h3 className="font-display text-xl sm:text-2xl font-bold text-white mb-2">
                        Add-On Services
                    </h3>
                    <p className="text-sm text-slate-500 mb-7">
                        Extend any package — priced transparently based on requirements.
                    </p>
                </Reveal>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {ADDONS.map((a, i) => (
                        <Reveal key={a.name} delay={(i % 4) * 0.06}>
                            <div
                                data-testid={`addon-${i}`}
                                className="h-full rounded-2xl border border-white/8 bg-[#0D131F] p-5 transition-all duration-300 hover:border-[#F5C518]/35 hover:-translate-y-0.5"
                            >
                                <a.icon size={18} className="text-[#F5C518] mb-3" />
                                <p className="text-sm font-medium text-white leading-snug">{a.name}</p>
                                <p className="mt-1.5 font-jbmono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                                    {a.note}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </div>
    </section>
);
